// IA Locator Section
// Interactive world map with country highlighting and accordion navigation

// ============================================================================
// Configuration
// ============================================================================

const CONFIG_SCRIPT = document.querySelector("[data-locator-config]");
const CONFIG_DATA = JSON.parse(CONFIG_SCRIPT.textContent || "{}");
const CONFIG = {
  WORLD_GEOJSON_URL: CONFIG_DATA.world_geojson_url,
  CONTINENT_DATA_URL: CONFIG_DATA.continent_data_url,
  
  COLORS: {
    SELECTED: "#1C2F45",
    DEFAULT: "#C8C8C8",
    BORDER: "#FFFFFF",
    BACKGROUND: "#FBFBFB",
  },
  
  MAP: {
    INITIAL_CENTER: [40, 0],
    INITIAL_CENTER_MOBILE: [30, -95],
    INITIAL_ZOOM: 2,
    MIN_ZOOM: 1,
    MAX_ZOOM: 6,
    MAX_BOUNDS: [[-85, -180], [85, 180]],
    BORDER_WIDTH: 1,
  },

  CONTINENT_ORDER: ["North America", "South America", "Europe", "Africa", "Asia", "Oceania", "Antarctica"],
};

// The ISO-3166 dataset ships these with a null region, so they resolve to no
// continent and drop out of the accordion unless mapped explicitly.
const CONTINENT_OVERRIDES = {
  TWN: "Asia",
  ATA: "Antarctica",
};

// ============================================================================
// Data Loading
// ============================================================================

let worldGeoJsonCache = null;
let continentMappingCache = null;

function loadWorldGeoJson() {
  if (!worldGeoJsonCache) {
    worldGeoJsonCache = fetch(CONFIG.WORLD_GEOJSON_URL)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load GeoJSON: ${response.status}`);
        }
        return response.json();
      });
  }
  return worldGeoJsonCache;
}

function loadContinentMapping() {
  if (!continentMappingCache) {
    continentMappingCache = fetch(CONFIG.CONTINENT_DATA_URL)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load continent data: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        const mapping = {};
        data.forEach(country => {
          const iso3 = country["alpha-3"];
          const region = country.region;
          const subRegion = country["sub-region"];
          
          if (iso3 && region) {
            mapping[iso3] = mapRegionToContinent(region, subRegion);
          }
        });
        return Object.assign(mapping, CONTINENT_OVERRIDES);
      });
  }
  return continentMappingCache;
}

function mapRegionToContinent(region, subRegion) {
  if (region === "Americas") {
    if (subRegion === "Northern America") {
      return "North America";
    }
    return "South America";
  }
  
  return region;
}

// ============================================================================
// Configuration Parser
// ============================================================================

async function parseLocatorConfig(rootElement) {
  const configScript = rootElement.querySelector("script[data-locator-config]");
  
  if (!configScript) {
    return { selectedCountries: [], countriesWithContinents: [] };
  }

  try {
    const config = JSON.parse(configScript.textContent || "{}");
    const countries = Array.isArray(config?.countries) ? config.countries : [];
    
    const selectedCountries = countries
      .map(country => country?.code)
      .filter(code => typeof code === "string")
      .map(code => code.trim().toUpperCase())
      .filter(Boolean);

    const continentMapping = await loadContinentMapping();
    
    const countriesWithContinents = countries.map(country => {
      const code = country?.code ? country.code.toUpperCase() : "";
      const continent = code ? (continentMapping[code] || "Unknown") : "Unknown";
      
      return {
        title: country?.name || country?.title || "",
        code: code,
        continent: continent,
        url: typeof country?.url === "string" ? country.url : "",
      };
    });

    return { selectedCountries, countriesWithContinents };
  } catch (error) {
    console.error("[IA Locator] Failed to parse config:", error);
    return { selectedCountries: [], countriesWithContinents: [] };
  }
}

// ============================================================================
// Custom Element
// ============================================================================

class IALocator extends HTMLElement {
  constructor() {
    super();
    this.map = null;
    this.geoJsonLayer = null;
  }

  async connectedCallback() {
    if (typeof L === "undefined") {
      console.error("[IA Locator] Leaflet library not loaded");
      return;
    }

    const mapContainer = this.querySelector("[data-locator-map]");
    if (!mapContainer) {
      console.error("[IA Locator] Map container not found");
      return;
    }

    const config = await parseLocatorConfig(this);
    console.log("[IA Locator] Config loaded:", config);
    
    this.initializeMap(mapContainer);
    await this.loadCountries(config.selectedCountries);
    this.renderAccordions(config.countriesWithContinents);
  }

  initializeMap(container) {
    if (!container.id) {
      container.id = `locator-${Math.random().toString(36).slice(2, 8)}`;
    }

    const initialCenter = window.innerWidth < 1024 ? CONFIG.MAP.INITIAL_CENTER_MOBILE : CONFIG.MAP.INITIAL_CENTER;

    this.map = L.map(container.id, {
      center: initialCenter,
      zoom: CONFIG.MAP.INITIAL_ZOOM,
      minZoom: CONFIG.MAP.MIN_ZOOM,
      maxZoom: CONFIG.MAP.MAX_ZOOM,
      maxBounds: CONFIG.MAP.MAX_BOUNDS,
      maxBoundsViscosity: 1.0,
      zoomControl: false,
      scrollWheelZoom: false,
      touchZoom: true,
      attributionControl: false,
    });

    this.addZoomControls();
  }

  async loadCountries(selectedCountryCodes) {
    const selectedCountries = new Set(selectedCountryCodes);

    try {
      const worldData = await loadWorldGeoJson();

      this.geoJsonLayer = L.geoJSON(worldData, {
        style: (feature) => this.getCountryStyle(feature, selectedCountries),
        onEachFeature: (feature, layer) => this.attachCountryClick(feature, layer),
      });

      this.geoJsonLayer.addTo(this.map);
    } catch (error) {
      console.error("[IA Locator] Failed to load world map:", error);
    }
  }

  getCountryStyle(feature, selectedCountries) {
    const countryCode = feature?.id;
    const isSelected = countryCode && selectedCountries.has(String(countryCode).toUpperCase());

    return {
      fillColor: isSelected ? CONFIG.COLORS.SELECTED : CONFIG.COLORS.DEFAULT,
      fillOpacity: 1,
      color: CONFIG.COLORS.BORDER,
      weight: CONFIG.MAP.BORDER_WIDTH,
    };
  }

  attachCountryClick(feature, layer) {
    layer.on("click", () => {
      const countryCode = feature?.id;
      if (countryCode) {
        this.openAccordionForCountry(String(countryCode).toUpperCase());
      }
    });
  }

  addZoomControls() {
    const controlContainer = L.DomUtil.create("div", "ia-locator__zoom-controls");
    
    const zoomInButton = this.createZoomButton("Zoom in", () => this.map.zoomIn());
    const zoomOutButton = this.createZoomButton("Zoom out", () => this.map.zoomOut());
    
    controlContainer.appendChild(zoomInButton);
    controlContainer.appendChild(zoomOutButton);

    const ZoomControl = L.Control.extend({
      onAdd: () => controlContainer,
    });

    new ZoomControl({ position: "bottomleft" }).addTo(this.map);
  }

  createZoomButton(title, onClick) {
    const button = L.DomUtil.create("button", "ia-locator__zoom-btn");
    button.title = title;
    button.type = "button";

    L.DomEvent.on(button, "click", (event) => {
      L.DomEvent.stopPropagation(event);
      L.DomEvent.preventDefault(event);
      onClick();
    });

    return button;
  }

  openAccordionForCountry(countryCode) {
    const accordionList = this.querySelector(".ia-locator__accordion-list");
    if (!accordionList) return;

    const countryItem = accordionList.querySelector(`[data-code="${countryCode}"]`);
    if (!countryItem) return;

    const accordion = countryItem.closest("ia-accordion-component");
    if (!accordion) return;

    const needsExpand = !accordion.classList.contains("ia-accordion--expanded");
    const body = accordion.querySelector(".ia-accordion__body");

    const doScrollAndHighlight = () => {
      this.scrollToCountryItem(countryItem);
      this.highlightCountryItem(countryItem);
    };

    if (needsExpand && body) {
      const onExpanded = () => {
        body.removeEventListener("transitionend", onExpanded);
        doScrollAndHighlight();
      };
      body.addEventListener("transitionend", onExpanded);
      this.expandAccordion(accordion);
    } else {
      doScrollAndHighlight();
    }
  }

  expandAccordion(accordion) {
    if (accordion.classList.contains("ia-accordion--expanded")) return;

    const toggleButton = accordion.querySelector("[data-toggle-accordion]");
    if (toggleButton) {
      toggleButton.click();
    }
  }

  scrollToCountryItem(item) {
    try {
      item.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (error) {
      item.scrollIntoView();
    }
  }

  highlightCountryItem(item) {
    item.style.transition = "background-color 0.3s";
    item.style.backgroundColor = "#f0f0f0";
    
    setTimeout(() => {
      item.style.backgroundColor = "";
    }, 1000);
  }

  renderAccordions(countries) {
    const accordionContainer = this.querySelector(".ia-locator__accordion-list");
    if (!accordionContainer) {
      console.error("[IA Locator] Accordion container not found");
      return;
    }

    accordionContainer.innerHTML = "";

    if (!countries || countries.length === 0) {
      console.warn("[IA Locator] No countries to render");
      return;
    }

    const countryGroups = this.groupCountriesByContinent(countries);
    console.log("[IA Locator] Country groups:", countryGroups);

    CONFIG.CONTINENT_ORDER.forEach((continent, index) => {
      const countriesInContinent = countryGroups[continent];
      if (!countriesInContinent || countriesInContinent.length === 0) return;

      const accordionHTML = this.createAccordionHTML(continent, countriesInContinent, index === 0);
      accordionContainer.insertAdjacentHTML("beforeend", accordionHTML);
    });

    const unplaced = Object.keys(countryGroups)
      .filter(continent => !CONFIG.CONTINENT_ORDER.includes(continent))
      .flatMap(continent => countryGroups[continent].map(country => country.code));

    if (unplaced.length > 0) {
      console.warn(`[IA Locator] No continent for ${unplaced.join(", ")} — not shown in the list.`);
    }
  }

  groupCountriesByContinent(countries) {
    const groups = {};
    
    countries.forEach(country => {
      const continent = country.continent || "Unknown";
      if (!groups[continent]) {
        groups[continent] = [];
      }
      groups[continent].push(country);
    });

    return groups;
  }

  createAccordionHTML(continentName, countries, isExpanded) {
    const expandedClass = isExpanded ? " ia-accordion--expanded" : "";
    const bodyStyle = isExpanded ? ' style="max-height: 1000px; opacity: 1;"' : "";
    
    const countriesHTML = countries
      .map(country => {
        const title = this.escapeHtml(country.title);
        const code = this.escapeHtml(country.code);
        const url = country.url ? this.escapeHtml(country.url) : "";
        if (url) {
          return `<a class="ia-locator__country-item" data-code="${code}" href="${url}">${title}</a>`;
        }
        return `<div class="ia-locator__country-item" data-code="${code}">${title}</div>`;
      })
      .join("");

    return `
      <ia-accordion-component class="ia-accordion ia-accordion--chevron${expandedClass}">
        <div class="ia-accordion__header" data-toggle-accordion>
          <h3 class="ia-accordion__title">${this.escapeHtml(continentName)}</h3>
          <span class="ia-accordion__expand-icon iconic-button">
            <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L7.5 7.5L14 1" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>
        <div class="ia-accordion__body"${bodyStyle}>
          <div class="ia-accordion__content">
            ${countriesHTML}
          </div>
        </div>
      </ia-accordion-component>
    `;
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define("ia-locator", IALocator);
